/**
 * RF-12.1 · Plan de mejoramiento y fortalecimiento
 * (`/api/planes-mejoramiento`).
 *
 * Acciones con responsable, fechas y avance. Las reglas de alerta C4 y C5 vigilan
 * las acciones vencidas y las que van a vencer con avance insuficiente.
 *
 * Capa de servicios: una función por operación del API. No lanza nunca —cada
 * función devuelve un valor neutro ante el fallo—, así que el llamador debe
 * comprobar el resultado, no envolver en try/catch.
 */

import { http, primerError } from '@/services/http'

// RF-12.1 · Plan de mejoramiento y fortalecimiento (formato institucional real).

export interface RecursosAccion {
  h?: boolean
  f?: boolean
  t?: boolean
}

/** Forma con la que el API de plan mejoramiento devuelve este dato. */
export interface EntregablesAccion {
  documento?: boolean
  listado?: boolean
  encuesta?: boolean
  estadistica?: boolean
  registro_fotografico?: boolean
  video?: boolean
  grupo_focal?: boolean
  otro?: boolean
  cual?: string | null
}

/** Forma con la que el API de plan mejoramiento devuelve este dato. */
export interface AccionMejora {
  id_accion: number
  id_plan: number
  id_componente: number
  id_indicador: number | null
  calificacion: number | null
  grado_cumplimiento: string | null
  clasificacion: 'fuerte' | 'debil' | 'muy_debil' | null
  causa_raiz: string | null
  actividad_general: string | null
  actividades_especificas: string | null
  tipo: 'MEJORAMIENTO' | 'FORTALECIMIENTO' | null
  prioridad: 'ALTA' | 'MEDIA' | 'BAJA' | null
  responsable_cargo: string | null
  fecha_inicio: string | null
  fecha_terminacion: string | null
  recursos: RecursosAccion | null
  presupuesto_proyectado: number | null
  presupuesto_ejecutado: number | null
  entregables: EntregablesAccion | null
  fuente_verificacion: string | null
  linea_base: string | null
  meta: string | null
  por_cumplir: string | null
  fecha_seguimiento: string | null
  estado: 'NO_INICIADO' | 'EN_DESARROLLO' | 'CUMPLIDO' | 'NO_CUMPLIDO' | 'ANULADO'
  avance_pct: number
  fecha_cierre: string | null
  observaciones: string | null
  componente?: { id_componente: number; nombre: string; padre?: { nombre: string } | null }
  indicador?: { id_indicador: number; enunciado: string } | null
}

/** Forma con la que el API de plan mejoramiento devuelve este dato. */
export interface PlanMejoramiento {
  id_plan: number
  id_ejecucion: number
  fecha_elaboracion: string | null
  facultad: string | null
  programa: string | null
  estado: 'borrador' | 'aprobado' | 'en_seguimiento' | 'cerrado'
  acciones?: AccionMejora[]
}

/**
 * Fila del export institucional (payload de `PlanMejoramientoService::paraExport`
 * en el backend): la acción más el origen desnormalizado (factor/característica/
 * aspecto). Tipada para que el generador xlsx reciba valores de celda válidos.
 */
export interface AccionExportFila {
  id_accion: number
  factor: string | null
  caracteristica: string | null
  aspecto: string | null
  calificacion: number | null
  grado_cumplimiento: string | null
  causa_raiz: string | null
  actividad_general: string | null
  actividades_especificas: string | null
  tipo: string | null
  prioridad: string | null
  responsable_cargo: string | null
  fecha_inicio: string | null
  fecha_terminacion: string | null
  recursos: RecursosAccion | null
  presupuesto_proyectado: number | null
  presupuesto_ejecutado: number | null
  entregables: EntregablesAccion | null
  fuente_verificacion: string | null
  linea_base: string | null
  meta: string | null
  fecha_seguimiento: string | null
  estado: string
  avance_pct: number
  fecha_cierre: string | null
  observaciones: string | null
}

/** Forma con la que el API de plan mejoramiento devuelve este dato. */
export interface PlanExport {
  cabecera: {
    titulo: string
    subtitulo: string
    fecha_elaboracion: string | null
    facultad: string | null
    programa: string | null
    periodo: string
  }
  acciones: AccionExportFila[]
  catalogos: {
    tipo_accion: string[]
    prioridad: string[]
    estado: Record<string, string>
  }
}

interface Resultado<T> {
  ok: boolean
  status: number
  data?: T
  error?: string
}

/**
 * Genera plan mejoramiento · `POST /api/ejecuciones`.
 *
 * @returns Resultado<PlanMejoramiento>
 */
export async function generarPlanService(idEjecucion: number): Promise<Resultado<PlanMejoramiento>> {
  const r = await http.post<PlanMejoramiento>(`/api/ejecuciones/${idEjecucion}/plan-mejoramiento`)
  return r.ok
    ? { ok: true, status: r.status, data: r.data }
    : { ok: false, status: r.status, error: primerError(r, 'No se pudo generar el plan de mejoramiento') }
}

/**
 * Consulta plan mejoramiento · `GET /api/ejecuciones`.
 *
 * @returns Resultado<PlanMejoramiento>
 */
export async function getPlanService(idEjecucion: number): Promise<Resultado<PlanMejoramiento>> {
  const r = await http.get<PlanMejoramiento>(`/api/ejecuciones/${idEjecucion}/plan-mejoramiento`)
  return r.ok
    ? { ok: true, status: r.status, data: r.data }
    : { ok: false, status: r.status, error: primerError(r, 'No se pudo cargar el plan de mejoramiento') }
}

/**
 * Actualiza plan mejoramiento · `PATCH /api/planes-mejoramiento`.
 *
 * @returns Resultado<PlanMejoramiento>
 */
export async function actualizarPlanService(idPlan: number, cambios: Partial<PlanMejoramiento>): Promise<Resultado<PlanMejoramiento>> {
  const r = await http.patch<PlanMejoramiento>(`/api/planes-mejoramiento/${idPlan}`, cambios)
  return r.ok
    ? { ok: true, status: r.status, data: r.data }
    : { ok: false, status: r.status, error: primerError(r, 'No se pudo actualizar el plan') }
}

/**
 * Actualiza plan mejoramiento · `PATCH /api/acciones-mejora`.
 *
 * @returns Resultado<AccionMejora>
 */
export async function actualizarAccionService(idAccion: number, cambios: Partial<AccionMejora>): Promise<Resultado<AccionMejora>> {
  const r = await http.patch<AccionMejora>(`/api/acciones-mejora/${idAccion}`, cambios)
  return r.ok
    ? { ok: true, status: r.status, data: r.data }
    : { ok: false, status: r.status, error: primerError(r, 'No se pudo actualizar la acción') }
}

/**
 * Crea plan mejoramiento · `PATCH /api/acciones-mejora`.
 *
 * @returns Resultado<AccionMejora>
 */
export async function registrarSeguimientoService(idAccion: number, cambios: Partial<AccionMejora>): Promise<Resultado<AccionMejora>> {
  const r = await http.patch<AccionMejora>(`/api/acciones-mejora/${idAccion}/seguimiento`, cambios)
  return r.ok
    ? { ok: true, status: r.status, data: r.data }
    : { ok: false, status: r.status, error: primerError(r, 'No se pudo registrar el seguimiento') }
}

/**
 * Consulta plan mejoramiento · `GET /api/planes-mejoramiento`.
 *
 * @returns Resultado<PlanExport>
 */
export async function getPlanExportService(idPlan: number): Promise<Resultado<PlanExport>> {
  const r = await http.get<PlanExport>(`/api/planes-mejoramiento/${idPlan}/export`)
  return r.ok
    ? { ok: true, status: r.status, data: r.data }
    : { ok: false, status: r.status, error: primerError(r, 'No se pudieron cargar los datos de exportación') }
}
